import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { DashboardProcessingItem } from '../../../store/reducer/DashboardReducer';
import {
  PiBriefcaseLight,
  PiFileMagnifyingGlassLight,
  PiSpinnerGapLight,
  PiArrowRightLight,
} from 'react-icons/pi';

interface ProcessingItemsProps {
  items: DashboardProcessingItem[];
}

const typeConfig: Record<string, { icon: React.ReactNode; route: string; color: string; bgColor: string }> = {
  matter: {
    icon: <PiBriefcaseLight className="w-4 h-4" />,
    route: '/matters',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
  },
  review: {
    icon: <PiFileMagnifyingGlassLight className="w-4 h-4" />,
    route: '/reviews',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/30',
  },
};

const formatElapsed = (startedAt: string | null): string => {
  if (!startedAt) return '';
  const started = new Date(startedAt);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - started.getTime()) / (1000 * 60));

  if (diffMin < 1) return 'Just started';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  return `${diffHrs}h ${diffMin % 60}m ago`;
};

const ProcessingItems: React.FC<ProcessingItemsProps> = ({ items }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const visibleItems = items.filter((item) => {
    if (item.type === 'matter') return hasSectionAccess('matters');
    if (item.type === 'review') return hasSectionAccess('review');
    return true;
  });

  if (visibleItems.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2.5">
          <PiSpinnerGapLight className="w-4 h-4 text-gray-800 dark:text-gray-300 animate-spin" />
          <h3 className="text-sm text-gray-900 dark:text-gray-100">
            {t('Currently Processing')}
          </h3>
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            {visibleItems.length}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
        {visibleItems.map((item) => {
          const config = typeConfig[item.type] || typeConfig.matter;
          return (
            <div
              key={`${item.type}-${item.uuid}`}
              className="flex items-center gap-3 p-3.5 rounded-xl bg-gray-50/80 dark:bg-gray-750/50 border border-gray-100 dark:border-gray-700/50
                hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-sm cursor-pointer transition-all"
              onClick={() => {
                if (item.uuid) history.push(`${config.route}/${item.uuid}`);
              }}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${config.bgColor} ${config.color} flex items-center justify-center`}>
                {config.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                  {item.title}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {formatElapsed(item.started_at)}
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="w-5 h-5 rounded-full border-2 border-blue-200 dark:border-blue-700 border-t-blue-500 dark:border-t-blue-400 animate-spin" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProcessingItems;
